import { IS_DEV, IS_STAGE, LOCAL_ENV } from '@/modules/env'
import { getPreferences } from '@/modules/storage'
import configDev from '@/appsync/config-dev'
import configStage from '@/appsync/config-stage'
import configProd from '@/appsync/config-prod'

let config
if (IS_DEV) {
  config = configDev
} else if (IS_STAGE) {
  config = configStage
} else {
  config = configProd
}

if (getPreferences('environment') !== null) {
  switch (getPreferences('environment')) {
    case LOCAL_ENV.STAGE:
      config = configStage
      break
    case LOCAL_ENV.DEV:
      config = configDev
      break
    default:
      config = configProd
      break
  }
}

const AWSJSON_FIELDS = new Set([
  'venue_types',
  'pos_types',
  'pos_integration_statuses',
  'partial_measuring_types',
  'intercom_product_tours',
  'sales_reps',
  'currencies',
  'custom_field_types',
  'timeline_filter_types',
  'permissions',
  'plan_features_upgrade',
  'measurements',
  'user_notification_types',
  'default_allergens',
  'languages',
  'timezones',
  'countries',
  'transfer_request_statuses',
  'taxes_schemes',
  'wisk_distributors_types',
  'nanonets_models'
])

function parseAppSyncResult(data) {
  if (!data) return null
  const parsed = {}
  for (const [key, value] of Object.entries(data)) {
    if (AWSJSON_FIELDS.has(key) && typeof value === 'string') {
      try {
        parsed[key] = JSON.parse(value)
      } catch {
        parsed[key] = value
      }
    } else {
      parsed[key] = value
    }
  }
  return parsed
}

async function appSyncRequest(query, variables = {}) {
  const response = await fetch(config.url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.apiKey
    },
    body: JSON.stringify({ query, variables })
  })
  const json = await response.json()
  if (json.errors) {
    console.error('AppSync error:', json.errors)
  }
  return json.data
}

export async function getGlobalSettings() {
  const query = `query {
    getGlobalSettings(id: "global") {
      id sync_provider venue_types pos_types pos_integration_statuses
      partial_measuring_types intercom_product_tours sales_reps currencies
      custom_field_types timeline_filter_types permissions plan_features_upgrade
      measurements user_notification_types default_allergens languages timezones
      countries transfer_request_statuses taxes_schemes wisk_distributors_types
      nanonets_models
    }
  }`
  const data = await appSyncRequest(query)
  return parseAppSyncResult(data?.getGlobalSettings)
}

export async function getWebSettings() {
  const query = `query {
    getWebSettings(id: "web") {
      id version
    }
  }`
  const data = await appSyncRequest(query)
  return data?.getWebSettings
}

export async function updateWebSettingsVersion(version) {
  const query = `mutation {
    updateWebSettings(input: { id: "web", version: "${version}" }) {
      id version
    }
  }`
  return appSyncRequest(query)
}

export async function getIosSettings() {
  const query = `query {
    getIosSettings(id: "ios") {
      id min_build_version shouldShowOnlyCurrentVariation
    }
  }`
  const data = await appSyncRequest(query)
  return data?.getIosSettings
}

let pollingIntervals = {}

export function subscribeToGlobalSettings(handler, intervalMs = 60000) {
  let active = true

  const poll = async () => {
    if (!active) return
    try {
      const result = await getGlobalSettings()
      if (active && result) {
        handler(result)
      }
    } catch (err) {
      console.error('AppSync global settings poll error:', err)
    }
  }

  poll()
  const id = setInterval(poll, intervalMs)
  pollingIntervals[id] = true

  return () => {
    active = false
    clearInterval(id)
    delete pollingIntervals[id]
  }
}

export function subscribeToWebSettings(handler, intervalMs = 60000) {
  let active = true

  const poll = async () => {
    if (!active) return
    try {
      const result = await getWebSettings()
      if (active && result) {
        handler(result)
      }
    } catch (err) {
      console.error('AppSync web settings poll error:', err)
    }
  }

  poll()
  const id = setInterval(poll, intervalMs)
  pollingIntervals[id] = true

  return () => {
    active = false
    clearInterval(id)
    delete pollingIntervals[id]
  }
}
